/**
 * Grouping Strategies
 *
 * Feature: 650-task-grouping
 *
 * Defines strategies for grouping tasks. Each strategy determines how tasks
 * are grouped together (by key) and how groups are labeled.
 */

import type { Task, TaskActionType } from '@/hooks/graphql/useTaskMutationsGraphQL';
import type { GroupingStrategy } from '@/types/task-groups';

/**
 * Human-readable labels for action types
 * Feature 651: Added COMMENT_ON_N_PHOTOS
 */
const ACTION_LABELS: Record<TaskActionType, string> = {
  VIEW_PHOTO: 'View photos',
  COMMENT_ON_PHOTO: 'Add comments',
  RATE_PHOTO: 'Rate photos',
  REPLY_TO_COMMENT: 'Reply to comments',
  COMMENT_ON_N_PHOTOS: 'Comment on photos',  // Feature 651
  MANUAL: 'Tasks',
};

/**
 * Strategy 1: Group by critique session + action type (most granular)
 *
 * Groups tasks that share the same sourceId AND actionType.
 * Example: All "rate photo" tasks for critique session cs123.
 */
export const critiqueActionStrategy: GroupingStrategy = {
  id: 'critique-action',
  name: 'Critique Actions',
  priority: 1,

  shouldGroup(task: Task): boolean {
    return (
      task.sourceId !== null &&
      ['critique_template', 'mention', 'feedback_options'].includes(task.sourceType)
    );
  },

  getGroupKey(task: Task): string | null {
    if (!this.shouldGroup(task)) return null;
    return `${task.sourceId}:${task.actionType}`;
  },

  getGroupLabel(groupKey: string, _tasks: Task[]): string {
    const parts = groupKey.split(':');
    const actionType = parts[parts.length - 1] as TaskActionType;
    return ACTION_LABELS[actionType] || 'Tasks';
  },

  getGroupUrl(_groupKey: string, tasks: Task[]): string {
    const firstPending = tasks.find(t => t.status === 'pending');
    return (firstPending || tasks[0]).targetUrl;
  },
};

/**
 * Strategy 2: Group all tasks for a critique session together
 *
 * Groups all tasks that share the same sourceId, regardless of action type.
 * Example: All tasks (view, rate, comment) for critique session cs123.
 */
export const critiqueAllStrategy: GroupingStrategy = {
  id: 'critique-all',
  name: 'Critique Sessions',
  priority: 2,

  shouldGroup(task: Task): boolean {
    return task.sourceId !== null;
  },

  getGroupKey(task: Task): string | null {
    if (!this.shouldGroup(task)) return null;
    return task.sourceId;
  },

  getGroupLabel(_groupKey: string, _tasks: Task[]): string {
    return 'Critique tasks';
  },

  getGroupUrl(_groupKey: string, tasks: Task[]): string {
    const firstPending = tasks.find(t => t.status === 'pending');
    return (firstPending || tasks[0]).targetUrl;
  },
};

/**
 * Strategy 3: Group by creation date
 *
 * Groups all tasks created on the same day.
 * Useful for daily task management views.
 */
export const dailyStrategy: GroupingStrategy = {
  id: 'daily',
  name: 'Daily',
  priority: 3,

  shouldGroup(): boolean {
    return true; // All tasks can be grouped by date
  },

  getGroupKey(task: Task): string | null {
    return task.createdAt.split('T')[0]; // YYYY-MM-DD
  },

  getGroupLabel(groupKey: string, _tasks: Task[]): string {
    const date = new Date(groupKey);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset times for comparison
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);

    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (groupKey === todayStr) return 'Today';
    if (groupKey === yesterdayStr) return 'Yesterday';

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  },

  getGroupUrl(_groupKey: string, tasks: Task[]): string {
    const firstPending = tasks.find(t => t.status === 'pending');
    return (firstPending || tasks[0]).targetUrl;
  },
};

/**
 * All available grouping strategies
 */
export const allStrategies: GroupingStrategy[] = [
  critiqueActionStrategy,
  critiqueAllStrategy,
  dailyStrategy,
];

/**
 * Default strategy to use when none is specified
 */
export const defaultStrategy = critiqueActionStrategy;
